import type { Ref } from 'vue';
import { onUnmounted, watchEffect } from 'vue';

type Particle = { x: number; y: number; vx: number; vy: number; r: number; color: string };

/** Hero 粒子连线动画 */
export function useHeroCanvas(canvasRef: Ref<HTMLCanvasElement | null>) {
  let animationId = 0;
  let cleanup: (() => void) | null = null;

  watchEffect((onCleanup) => {
    const canvas = canvasRef.value;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let particles: Particle[] = [];
    const isLowEnd = Boolean(navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2);
    const PARTICLE_COUNT = isLowEnd ? 0 : 80;
    const CONNECTION_DIST = 140;
    let mouseX = -1000;
    let mouseY = -1000;

    function resize() {
      width = canvas!.width = canvas!.offsetWidth;
      height = canvas!.height = canvas!.offsetHeight;
    }

    function createParticles() {
      particles = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
          r: Math.random() * 1.5 + 0.5,
          color: Math.random() > 0.5 ? 'rgba(0,113,227,' : 'rgba(41,151,255,'
        });
      }
    }

    function draw() {
      ctx!.clearRect(0, 0, width, height);
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;
        if (p.y < 0) p.y = height;
        if (p.y > height) p.y = 0;

        const dx = p.x - mouseX;
        const dy = p.y - mouseY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 120 && dist > 0) {
          const force = ((120 - dist) / 120) * 0.8;
          p.x += (dx / dist) * force;
          p.y += (dy / dist) * force;
        }

        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx!.fillStyle = p.color + '0.6)';
        ctx!.fill();

        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const cdx = p.x - p2.x;
          const cdy = p.y - p2.y;
          const cdist = Math.sqrt(cdx * cdx + cdy * cdy);
          if (cdist < CONNECTION_DIST) {
            const alpha = (1 - cdist / CONNECTION_DIST) * 0.15;
            ctx!.beginPath();
            ctx!.moveTo(p.x, p.y);
            ctx!.lineTo(p2.x, p2.y);
            ctx!.strokeStyle = `rgba(0,113,227,${alpha})`;
            ctx!.lineWidth = 0.5;
            ctx!.stroke();
          }
        }
      }
      animationId = requestAnimationFrame(draw);
    }

    const onMove = (e: MouseEvent) => {
      const rect = canvas!.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    };
    const onLeave = () => {
      mouseX = -1000;
      mouseY = -1000;
    };

    let resizeHandler: (() => void) | null = null;

    if (PARTICLE_COUNT > 0) {
      resize();
      createParticles();
      draw();
      resizeHandler = () => {
        resize();
        createParticles();
      };
      window.addEventListener('resize', resizeHandler);
      canvas.addEventListener('mousemove', onMove);
      canvas.addEventListener('mouseleave', onLeave);
    } else {
      canvas.style.background =
        'radial-gradient(ellipse at 50% 40%, rgba(0,113,227,0.06) 0%, transparent 60%)';
    }

    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(animationId);
      } else if (PARTICLE_COUNT > 0) {
        draw();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    cleanup = () => {
      cancelAnimationFrame(animationId);
      if (resizeHandler) window.removeEventListener('resize', resizeHandler);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };

    onCleanup(() => {
      cleanup?.();
      cleanup = null;
    });
  });

  onUnmounted(() => {
    cleanup?.();
  });
}
